'use client';

import {
  isNoOutcomeTitle,
  isYesOutcomeTitle,
  type OutcomeHueName,
  type OutcomePaletteStep,
  type OutcomeRole,
  outcomeHueFromTitle,
  outcomeHuesForMarket,
  outcomeHueToStepVar,
  outcomeHueToVar,
} from '@/lib/chart/outcome-hue';

// Re-export for consumers (FQ-119: use this hook or `@/lib/chart/outcome-hue`).
export { outcomeHueToStepVar, outcomeHueToVar };
export type { OutcomeHueName, OutcomePaletteStep, OutcomeRole };

/**
 * Tailwind class bundle returned for a single outcome.
 *
 * - `outline` / `fill` / `hoverFill` / `text` → bar + label pattern.
 * - `cta` / `ctaForeground` → solid CTA button pattern.
 * - `hue` → resolved `OutcomeHueName` (caller can compose other utilities,
 *   e.g. `border-outcome-${hue}-fill`).
 *
 * Class strings are theme-independent; the cascade resolves the actual color
 * via `:root` (light) and `.dark` (dark) blocks in `globals.css`.
 */
export interface OutcomeColorClasses {
  outline: string;
  fill: string;
  /** `hover:bg-outcome-{hue}-fill` — matches bar fill on button hover (safelisted for Tailwind v4 scan). */
  hoverFill: string;
  /** `hover:bg-outcome-{hue}-outline` — tinted hover for buttons that rest on `fill` (safelisted). */
  hoverOutline: string;
  text: string;
  cta: string;
  ctaForeground: string;
  hue: OutcomeHueName;
}

interface UseOutcomeColorOptions {
  /** Stable parent address; omit only for anonymous design previews. */
  marketPubkey?: string | undefined;
  /**
   * Per-outcome hue override map keyed by `outcome.index`. When set, replaces
   * the palette slot before per-market collision-avoidance runs. Yes/No anchors
   * take precedence. Overrides participate in the strict-uniqueness check.
   */
  overrides?: Record<number, OutcomeHueName>;
}

interface OutcomeInput {
  title: string;
  index: number;
}

// ─── Referential-stability caches ───────────────────────────────────────────
// `useOutcomeColor` is a PURE function (no React hooks called) — its outputs
// depend only on the market address, titles, indices, and overrides. To let downstream
// memoization (the React Compiler in consumers, plus per-leaf field hooks)
// actually do its job, identical inputs must return the SAME refs. Without
// this, `CardBarGrid` (which reads `outcomes` — a fresh array of fresh
// objects on every conviction tick) was producing a fresh `c` prop for every
// bar, churning ~561 re-renders / 40 s of `MarketRowOutcomeLabelButton`.
//
// These are MODULE-LEVEL pure-function caches — NOT React hooks — so they
// are outside FQ-140's scope (the rule targets the React memoization hooks
// banned in `.claude/rules/frontend-conventions.md`). Bounded by the size of
// the palette + the count of distinct markets / outcome sets per session.
const hueClassesCache: Map<OutcomeHueName, OutcomeColorClasses> = new Map<
  OutcomeHueName,
  OutcomeColorClasses
>();
function classesForHue(hue: OutcomeHueName): OutcomeColorClasses {
  const cached = hueClassesCache.get(hue);
  if (cached !== undefined) return cached;
  const result: OutcomeColorClasses = {
    outline: `bg-outcome-${hue}-outline`,
    fill: `bg-outcome-${hue}-fill`,
    hoverFill: `hover:bg-outcome-${hue}-fill`,
    hoverOutline: `hover:bg-outcome-${hue}-outline`,
    text: `text-outcome-${hue}-text`,
    cta: `bg-outcome-${hue}-cta`,
    ctaForeground: `text-outcome-${hue}-cta-foreground`,
    hue,
  };
  hueClassesCache.set(hue, result);
  return result;
}

// Defensive soft cap — outcome sets per session are O(markets) so realistic
// usage stays well under this. Hitting it would indicate an unbounded source
// of new titles; a full clear is cheap and self-healing.
const ARRAY_RESULT_CACHE_MAX = 1024;
const arrayResultCache: Map<string, readonly OutcomeColorClasses[]> = new Map<
  string,
  readonly OutcomeColorClasses[]
>();

/** Stable content-signature of the array-form inputs + overrides. */
function arrayResultCacheKey(
  outcomes: ReadonlyArray<OutcomeInput>,
  overrides: Record<number, OutcomeHueName> | undefined,
  marketPubkey: string | undefined,
): string {
  const itemsKey = outcomes.map((o) => [o.title, o.index]);
  const overridesKey =
    overrides === undefined || Object.keys(overrides).length === 0 ? '' : JSON.stringify(overrides);
  return JSON.stringify([marketPubkey, itemsKey, overridesKey]);
}

/**
 * Resolve outcome color classes for one outcome OR an array of outcomes.
 *
 * **Yes/No reservation**: outcomes whose label is `"yes"` or a derivative
 * (`"Yes — ban them"`, `"Yes, definitely"`) always resolve to the
 * **outcome-green** hue family on every surface; `"no"`-derivatives
 * (`"No — let people play"`) resolve to **outcome-red**. Other outcomes use
 * canonical-index slots in the market's seeded palette. The
 * reservation applies on every class field — `outline`, `fill`, `text`, `cta`,
 * and `ctaForeground` all root on the green or red scale.
 *
 * **Single-outcome form** returns one `OutcomeColorClasses`. **Array form**
 * returns a parallel array with strict per-market hue uniqueness enforced
 * (no two outcomes in the same market share a hue; throws on > 16 outcomes).
 *
 * **Theme handling**: the hook is theme-independent — it returns Tailwind
 * class strings; the cascade resolves light/dark via the semantic-token block
 * in `globals.css`. There is no `useTheme()` call, no `dark:` prefix needed.
 *
 * @example Bar pattern (most common)
 * ```tsx
 * const { outline, fill, text } = useOutcomeColor({ title, index });
 * return (
 *   <div className={cn('rounded-md p-2', outline)}>
 *     <div className={cn('h-6 rounded-sm', fill)} style={{ width: `${share}%` }}>
 *       <span className={cn('px-2 text-caption font-medium', text)}>{title}</span>
 *     </div>
 *   </div>
 * );
 * ```
 *
 * @example CTA button (trade panel, opinion bet)
 * ```tsx
 * const { cta, ctaForeground } = useOutcomeColor({ title, index });
 * return <AnimatedButton className={cn(cta, ctaForeground)}>Buy {title}</AnimatedButton>;
 * ```
 *
 * @example Chart line stroke (recharts / lightweight-charts)
 * ```tsx
 * const { hue } = useOutcomeColor({ title, index });
 * return <Line stroke={outcomeHueToStepVar(hue, 400)} />;
 * ```
 *
 * @see Engineer onboarding: `frontend/specs/035-outcome-color-palette/supplementary/quickstart.md`
 * @see Palette generator: `frontend/scripts/generate-outcome-palette.py`
 * @see Spec FR-015 / FR-017 / FR-023a–d for the contract details.
 */
export function useOutcomeColor(
  outcome: OutcomeInput,
  options?: UseOutcomeColorOptions,
): OutcomeColorClasses;
export function useOutcomeColor(
  outcomes: ReadonlyArray<OutcomeInput>,
  options?: UseOutcomeColorOptions,
): readonly OutcomeColorClasses[];
export function useOutcomeColor(
  input: OutcomeInput | ReadonlyArray<OutcomeInput>,
  options?: UseOutcomeColorOptions,
): OutcomeColorClasses | readonly OutcomeColorClasses[] {
  if (Array.isArray(input)) {
    if (input.length === 0) return [];

    const overrides = options?.overrides;
    const cacheKey = arrayResultCacheKey(input, overrides, options?.marketPubkey);
    const cached = arrayResultCache.get(cacheKey);
    if (cached !== undefined) return cached;

    const hues =
      overrides === undefined || Object.keys(overrides).length === 0
        ? outcomeHuesForMarket(input, 'precise-order', options?.marketPubkey)
        : resolveWithOverrides(input, overrides, options?.marketPubkey);
    const result: readonly OutcomeColorClasses[] = hues.map(classesForHue);

    if (arrayResultCache.size >= ARRAY_RESULT_CACHE_MAX) arrayResultCache.clear();
    arrayResultCache.set(cacheKey, result);
    return result;
  }

  const single = input as OutcomeInput;
  const overrides = options?.overrides;
  const overrideHue = overrides?.[single.index];
  if (
    overrideHue !== undefined &&
    !isYesOutcomeTitle(single.title) &&
    !isNoOutcomeTitle(single.title)
  ) {
    return classesForHue(overrideHue);
  }
  // Precise-order default: the resolver reserves green/red for Yes/No-derivative
  // labels and draws every other outcome from the non-reserved sequence by
  // position — so `title` AND `index` both matter.
  return classesForHue(
    outcomeHueFromTitle(single.title, single.index, 'precise-order', options?.marketPubkey),
  );
}

/**
 * Apply overrides to a market with strict uniqueness preserved.
 *
 * Yes/No anchors are pinned before explicit overrides. Remaining outcomes use
 * the market's canonical-index palette and walk to a free hue on collision.
 */
function resolveWithOverrides(
  outcomes: ReadonlyArray<OutcomeInput>,
  overrides: Record<number, OutcomeHueName>,
  marketPubkey?: string,
): readonly OutcomeHueName[] {
  if (outcomes.length > 16) {
    throw new Error(
      `useOutcomeColor: cannot assign unique hues to ${String(outcomes.length)} outcomes (max 16)`,
    );
  }

  const sorted = outcomes.toSorted((a, b) => a.index - b.index);
  const used = new Set<OutcomeHueName>();
  const assigned = new Map<number, OutcomeHueName>();

  // Semantic labels retain their meaning even on surfaces with explicit colors.
  for (const o of sorted) {
    const anchor = isYesOutcomeTitle(o.title)
      ? 'green'
      : isNoOutcomeTitle(o.title)
        ? 'red'
        : undefined;
    if (anchor === undefined || used.has(anchor)) continue;
    assigned.set(o.index, anchor);
    used.add(anchor);
  }

  // Pre-pass 1: explicit overrides claim their hue first (in index order),
  // bumping later overrides on collision via the 16-hue ring walk.
  for (const o of sorted) {
    if (assigned.has(o.index)) continue;
    const override = overrides[o.index];
    if (override === undefined) continue;
    let hue = override;
    if (used.has(hue)) {
      const startIdx = OUTCOME_HUES_LOCAL.indexOf(hue);
      let found = false;
      for (let step = 1; step < OUTCOME_HUES_LOCAL.length; step++) {
        const candidate = OUTCOME_HUES_LOCAL[(startIdx + step) % OUTCOME_HUES_LOCAL.length];
        if (candidate !== undefined && !used.has(candidate)) {
          hue = candidate;
          found = true;
          break;
        }
      }
      if (!found) {
        throw new Error(
          `useOutcomeColor: collision-walk exhausted for override at outcome index ${String(o.index)}`,
        );
      }
    }
    used.add(hue);
    assigned.set(o.index, hue);
  }

  // Pass 2: precise-order fill for non-overridden outcomes. Walk the ring on
  // collision with an already-claimed override hue.
  for (const o of sorted) {
    if (assigned.has(o.index)) continue;
    let hue = outcomeHueFromTitle(o.title, o.index, 'precise-order', marketPubkey);
    if (used.has(hue)) {
      const startIdx = OUTCOME_HUES_LOCAL.indexOf(hue);
      let found = false;
      for (let step = 1; step < OUTCOME_HUES_LOCAL.length; step++) {
        const candidate = OUTCOME_HUES_LOCAL[(startIdx + step) % OUTCOME_HUES_LOCAL.length];
        if (candidate !== undefined && !used.has(candidate)) {
          hue = candidate;
          found = true;
          break;
        }
      }
      if (!found) {
        throw new Error(
          `useOutcomeColor: collision-walk exhausted for outcome index ${String(o.index)}`,
        );
      }
    }
    used.add(hue);
    assigned.set(o.index, hue);
  }

  return outcomes.map((o) => {
    const hue = assigned.get(o.index);
    if (hue === undefined) {
      throw new Error(`useOutcomeColor: missing assignment for outcome index ${String(o.index)}`);
    }
    return hue;
  });
}

// Local copy of OUTCOME_HUES so the override resolver doesn't import-loop
// through outcome-colors.ts. Kept in sync via a runtime sanity check.
const OUTCOME_HUES_LOCAL: readonly OutcomeHueName[] = [
  'red',
  'orange',
  'amber',
  'yellow',
  'lime',
  'green',
  'emerald',
  'teal',
  'cyan',
  'sky',
  'blue',
  'indigo',
  'violet',
  'purple',
  'fuchsia',
  'pink',
];
